import {
  ASK_PAPER_CREDITS_PER_MONTH_ANONYMOUS_USER,
  ASK_PAPER_CREDITS_PER_MONTH_FREE_USER,
  PRO_SEARCH_CREDITS_PER_MONTH_ANONYMOUS_USER,
  PRO_SEARCH_CREDITS_PER_MONTH_FREE_USER,
  QUICK_SEARCH_CREDITS_PER_MONTH_ANONYMOUS_USER,
  QUICK_SEARCH_CREDITS_PER_MONTH_FREE_USER,
  SNAPSHOT_CREDITS_PER_MONTH_ANONYMOUS_USER,
  SNAPSHOT_CREDITS_PER_MONTH_FREE_USER,
} from "constants/config";
import { getTotalDeepSearchCredits } from "helpers/creditCalculations";
import { getMaxCreditsPerMonth } from "constants/searchModeConfig";
import { AskPaperThreadMessage, SearchMode } from "helpers/api";
import {
  calculateAskPaperMessageCountForPaper,
  getCreditRefreshDate,
  hashQuery,
} from "helpers/subscription";
import { useRouter } from "next/router";
import { useCallback, useMemo } from "react";
import { CreditedFeature } from "store/slices/subscription";
import useSubscription from "./useSubscription";
import { useUserUsage } from "./useUserUsage";

const useCredits = (threadedSearchId?: string) => {
  const router = useRouter();
  const {
    usage,
    isUsageSet,
    isNotSignedIn,
    appendConsensusSnapshotUsedId,
    appendProSearchUsedId,
    appendStudyDetailsUsedPaperId,
    updateAskPaperMessageCountForPaper,
    appendQuickSearchUsedId,
    appendDeepSearchUsedId,
  } = useUserUsage();

  const searchId = useMemo(() => {
    if (threadedSearchId) return threadedSearchId;
    if (router.query.q === undefined) return undefined;
    return hashQuery(router.query);
  }, [router.query, threadedSearchId]);

  const {
    isSubscriptionPremium: isPremium,
    isSubscriptionLoaded,
    subscriptionTier,
  } = useSubscription();

  const spendCreditsOnConsensusMeterSnapshot = useCallback(() => {
    if (searchId === undefined)
      throw new Error("Error spending credit: query not set");
    if (isPremium) return;
    appendConsensusSnapshotUsedId(searchId);
  }, [appendConsensusSnapshotUsedId, searchId, isPremium]);

  const spendCreditsOnAskPaper = useCallback(
    (paperId: string, messages: AskPaperThreadMessage[]) => {
      if (isPremium) return;
      const previousMessagesForPaper = calculateAskPaperMessageCountForPaper(
        messages,
        usage.last_reset_date
      );
      // The function is triggered upon receiving a new message, which doesn't exist in the list.
      // So, add 1 to the usage data to account for it.
      updateAskPaperMessageCountForPaper({
        [paperId]: previousMessagesForPaper + 1,
      });
    },
    [updateAskPaperMessageCountForPaper, usage.last_reset_date, isPremium]
  );

  const spendCreditOnStudyDetails = useCallback(
    (paperId: string) => {
      if (isPremium) return;
      appendStudyDetailsUsedPaperId(paperId);
    },
    [appendStudyDetailsUsedPaperId, isPremium]
  );

  const spendCreditOnSearch = (searchId: string, searchMode: SearchMode) => {
    const limit = getMaxCreditsPerMonth(
      searchMode,
      isNotSignedIn,
      subscriptionTier
    );

    if (limit === null) return; // Unlimited credits, no need to track uses.

    switch (searchMode) {
      case SearchMode.PRO_RESEARCH:
        appendDeepSearchUsedId(searchId);
        return;
      case SearchMode.PRO_ANALYSIS:
        appendProSearchUsedId(searchId);
        return;
      case SearchMode.SUMMARY:
        appendQuickSearchUsedId(searchId);
        return;
    }
  };

  const proSearchWasPreviouslyRunOnQuery = useMemo(() => {
    return (
      searchId !== undefined && usage.used_pro_search_queries.includes(searchId)
    );
  }, [usage.used_pro_search_queries, searchId]);

  const consensusSnapshotWasPreviouslyRunOnQuery = useMemo(() => {
    return (
      searchId !== undefined &&
      usage.used_consensus_snapshot_queries.includes(searchId)
    );
  }, [usage.used_consensus_snapshot_queries, searchId]);

  const wasStudySnapshotPreviouslyRunOnPaperId = useCallback(
    (paperId: string) => {
      return usage.used_study_paper_ids.includes(paperId);
    },
    [usage.used_study_paper_ids]
  );

  const proSearchCreditsRemaining = useMemo(() => {
    if (!isUsageSet) return 0;
    if (isPremium) return null;

    const PRO_SEARCH_CREDITS_PER_MONTH = isNotSignedIn
      ? PRO_SEARCH_CREDITS_PER_MONTH_ANONYMOUS_USER
      : PRO_SEARCH_CREDITS_PER_MONTH_FREE_USER;

    return Math.max(
      0,
      PRO_SEARCH_CREDITS_PER_MONTH - usage.used_pro_search_queries.length
    );
  }, [usage, isNotSignedIn, isPremium, isUsageSet]);

  const isOutOfProSearchCredits = useMemo(() => {
    return proSearchCreditsRemaining !== null && proSearchCreditsRemaining <= 0;
  }, [proSearchCreditsRemaining]);

  // Snapshot credits combine the number of study snapshots and consensus snapshots
  const snapshotCreditsRemaining = useMemo(() => {
    if (isPremium) return null;
    if (!isUsageSet) return 0;
    const SNAPSHOT_CREDITS_PER_MONTH = isNotSignedIn
      ? SNAPSHOT_CREDITS_PER_MONTH_ANONYMOUS_USER
      : SNAPSHOT_CREDITS_PER_MONTH_FREE_USER;

    return Math.max(
      0,
      SNAPSHOT_CREDITS_PER_MONTH -
        usage.used_study_paper_ids.length -
        usage.used_consensus_snapshot_queries.length
    );
  }, [usage, isNotSignedIn, isPremium, isUsageSet]);

  const isOutOfSnapshotCredits = useMemo(() => {
    return snapshotCreditsRemaining !== null && snapshotCreditsRemaining <= 0;
  }, [snapshotCreditsRemaining]);

  const askPaperCreditsRemaining = useMemo(() => {
    if (isPremium) return null;
    if (!isUsageSet) return 0;

    const ASK_PAPER_CREDITS_PER_MONTH = isNotSignedIn
      ? ASK_PAPER_CREDITS_PER_MONTH_ANONYMOUS_USER
      : ASK_PAPER_CREDITS_PER_MONTH_FREE_USER;

    // Sum up all the values in the ask_paper_messages_per_paper_count map
    const askPaperCreditsUsed = Object.values(
      usage.ask_paper_messages_per_paper_count
    ).reduce((total, count) => total + count, 0);

    return Math.max(0, ASK_PAPER_CREDITS_PER_MONTH - askPaperCreditsUsed);
  }, [usage, isNotSignedIn, isPremium, isUsageSet]);

  const isOutOfAskPaperCredits = useMemo(() => {
    return askPaperCreditsRemaining !== null && askPaperCreditsRemaining <= 0;
  }, [askPaperCreditsRemaining]);

  const quickSearchCreditsRemaining = useMemo(() => {
    if (!isUsageSet) return 0;
    if (isPremium) return null;

    const QUICK_SEARCH_CREDITS_PER_MONTH = isNotSignedIn
      ? QUICK_SEARCH_CREDITS_PER_MONTH_ANONYMOUS_USER
      : QUICK_SEARCH_CREDITS_PER_MONTH_FREE_USER;

    // If null, it means unlimited credits
    if (QUICK_SEARCH_CREDITS_PER_MONTH === null) return null;

    return Math.max(
      0,
      QUICK_SEARCH_CREDITS_PER_MONTH - usage.used_quick_search_queries.length
    );
  }, [usage, isNotSignedIn, isPremium, isUsageSet]);

  const isOutOfQuickSearchCredits = useMemo(() => {
    return (
      quickSearchCreditsRemaining !== null && quickSearchCreditsRemaining <= 0
    );
  }, [quickSearchCreditsRemaining]);

  const deepSearchCreditsRemaining = useMemo(() => {
    if (!isUsageSet) return 0;

    const bonusDeepSearchCredits = usage.bonus_deep_search_credits || 0;
    const totalCredits = getTotalDeepSearchCredits(
      isNotSignedIn,
      subscriptionTier,
      bonusDeepSearchCredits
    );

    return totalCredits === null
      ? null
      : Math.max(0, totalCredits - usage.used_deep_search_queries.length);
  }, [isNotSignedIn, subscriptionTier, usage, isUsageSet]);

  const isOutOfDeepSearchCredits = useMemo(() => {
    return (
      deepSearchCreditsRemaining !== null && deepSearchCreditsRemaining <= 0
    );
  }, [deepSearchCreditsRemaining]);

  const ensureNonNegative = (value: number | null): number | null => {
    return value !== null ? Math.max(0, value) : null;
  };

  const searchModeCreditsRemaining = (searchMode: SearchMode) => {
    switch (searchMode) {
      case SearchMode.PRO_RESEARCH:
        return ensureNonNegative(deepSearchCreditsRemaining);
      case SearchMode.PRO_ANALYSIS:
        return ensureNonNegative(proSearchCreditsRemaining);
      case SearchMode.SUMMARY:
        return ensureNonNegative(quickSearchCreditsRemaining);
      default:
        return null;
    }
  };

  const creditRefreshDate = getCreditRefreshDate(usage);

  // Get total credits for any search mode (including bonus for Deep Search)
  const getTotalCreditsForMode = useCallback(
    (searchMode: SearchMode): number | null => {
      if (searchMode === SearchMode.PRO_RESEARCH) {
        const bonusDeepSearchCredits = usage?.bonus_deep_search_credits || 0;
        return getTotalDeepSearchCredits(
          isNotSignedIn,
          subscriptionTier,
          bonusDeepSearchCredits
        );
      }

      return getMaxCreditsPerMonth(searchMode, isNotSignedIn, subscriptionTier);
    },
    [isNotSignedIn, subscriptionTier, usage]
  );

  const featureToCreditsRemainingMap: Record<
    | CreditedFeature.ASK_PAPER
    | CreditedFeature.STUDY_SNAPSHOT
    | CreditedFeature.PRO_SEARCH
    | CreditedFeature.CONSENSUS_SNAPSHOT
    | CreditedFeature.QUICK_SEARCH
    | CreditedFeature.DEEP_SEARCH,
    number | null
  > = {
    [CreditedFeature.ASK_PAPER]: askPaperCreditsRemaining,
    [CreditedFeature.STUDY_SNAPSHOT]: snapshotCreditsRemaining,
    [CreditedFeature.PRO_SEARCH]: proSearchCreditsRemaining,
    [CreditedFeature.CONSENSUS_SNAPSHOT]: snapshotCreditsRemaining,
    [CreditedFeature.QUICK_SEARCH]: quickSearchCreditsRemaining,
    [CreditedFeature.DEEP_SEARCH]: deepSearchCreditsRemaining,
  };

  return {
    askPaperCreditsRemaining,
    consensusSnapshotWasPreviouslyRunOnQuery,
    creditRefreshDate,
    deepSearchCreditsRemaining,
    featureToCreditsRemainingMap,
    getTotalCreditsForMode,
    isNotSignedIn,
    isOutOfAskPaperCredits,
    isOutOfDeepSearchCredits,
    isOutOfProSearchCredits,
    isOutOfQuickSearchCredits,
    isOutOfSnapshotCredits,
    isPremium: Boolean(isPremium),
    isSubscriptionLoaded,
    isUsageSet,
    proSearchCreditsRemaining,
    proSearchWasPreviouslyRunOnQuery,
    quickSearchCreditsRemaining,
    searchModeCreditsRemaining,
    snapshotCreditsRemaining,
    spendCreditOnSearch,
    spendCreditOnStudyDetails,
    spendCreditsOnAskPaper,
    spendCreditsOnConsensusMeterSnapshot,
    wasStudySnapshotPreviouslyRunOnPaperId,
  };
};

export default useCredits;
